<!--
  QaAudioConnection.vue — the "Audio connection" section at the bottom of a graded call in Care Management > QA.

  One row per participant from Twilio Voice Insights (GET telehealth-phone-sessions/{uuid}/call-quality/):
  status, MOS, packet loss, jitter, round trip and mic floor, with Twilio's tags as flags. It measures the
  connection, not how the voice sounded, so it complements Play audio rather than replacing it.
  Calls placed before Voice Insights Advanced Features was on have no summary and show "No data".
-->
<template>
  <div style="padding: 10px 14px; border-top: 1px solid #E8E9E9; background: #FFFFFF;" data-test="audio-connection">
    <div style="display: flex; align-items: baseline; gap: 8px; margin-bottom: 6px;">
      <span style="font-weight: 600; color: #353537;">Audio connection</span>
      <span style="font-size: 0.86em; color: #A5A5A6;">network quality per participant, from Twilio</span>
    </div>
    <div v-if="loading" class="h-3 w-1/2 bg-gray-200 rounded animate-pulse" data-test="audio-connection-loading" />
    <div v-else-if="error" style="font-size: 0.86em; color: #B91C1C;" data-test="audio-connection-error">{{ error }}</div>
    <table v-else style="width: 100%; font-size: 0.86em; font-variant-numeric: tabular-nums; border-collapse: collapse;">
      <thead>
        <tr style="color: #6B7280; text-align: left;">
          <th style="font-weight: 500; padding: 2px 8px 2px 0;">Participant</th>
          <th style="font-weight: 500; padding: 2px 8px;">Status</th>
          <th style="font-weight: 500; padding: 2px 8px;" title="Mean opinion score, 1 to 5. Above 4 is good.">MOS</th>
          <th style="font-weight: 500; padding: 2px 8px;">Packet loss</th>
          <th style="font-weight: 500; padding: 2px 8px;">Jitter</th>
          <th style="font-weight: 500; padding: 2px 8px;">Round trip</th>
          <th style="font-weight: 500; padding: 2px 8px;" title="Quietest mic input level. A high floor means constant background noise.">Mic floor</th>
          <th style="font-weight: 500; padding: 2px 8px;">Flags</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.id" style="border-top: 1px solid #F0F0F1;" :data-test="`audio-connection-row-${row.id}`">
          <td style="padding: 4px 8px 4px 0; text-transform: capitalize;">{{ row.label }}</td>
          <td style="padding: 4px 8px; color: #6B7280;">{{ row.status.replace('_', ' ') }}</td>
          <template v-if="row.stream">
            <td :style="cell(mosColour(row.stream.mos_avg))" data-test="mos">
              {{ fmt(row.stream.mos_avg, 2) }}<span v-if="row.stream.mos_min !== null" style="color: #A5A5A6;"> (min {{ fmt(row.stream.mos_min, 1) }})</span>
            </td>
            <td :style="cell(lossColour(row.stream.packets_loss_percentage))" data-test="loss">{{ pct(row.stream.packets_loss_percentage) }}</td>
            <td :style="cell(jitterColour(row.stream.jitter_avg))">{{ ms(row.stream.jitter_avg) }}</td>
            <td :style="cell(rttColour(row.stream.rtt_avg ?? row.stream.latency_avg))">{{ ms(row.stream.rtt_avg ?? row.stream.latency_avg) }}</td>
            <td :style="cell()">{{ fmt(row.stream.audio_in_min, 0) }}</td>
          </template>
          <td v-else colspan="5" style="padding: 4px 8px; color: #A5A5A6;" data-test="no-data">No data</td>
          <td style="padding: 4px 8px;">
            <span
              v-for="tag in row.tags" :key="tag"
              :style="tagStyle(tag)" :data-test="`tag-${tag}`"
            >{{ tag.replaceAll('_', ' ') }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { getCallQuality } from '@/services/api/voiceApi';

// Edges in the order they best describe a participant: the browser (sdk) leg reports MOS and mic level.
const EDGE_ORDER = ['sdk', 'client', 'carrier', 'sip'];
const AMBER = '#B45309';
// Twilio tags that mean a problem. Others (e.g. hd_audio, downsampled) only describe the call.
const PROBLEM_TAG = /^(high_|low_)|silence|one_way|dropped|error|failed|congestion|ice_/;
const RED = '#B91C1C';

export default {
  name: 'QaAudioConnection',
  props: {
    sessionUuid: { type: String, required: true },
  },
  data() {
    return { loading: true, error: '', participants: [] };
  },
  computed: {
    rows() {
      return this.participants.map((p) => {
        const edges = p.summary?.edges || [];
        const edge = EDGE_ORDER.map((name) => edges.find((e) => e.edge === name && e.inbound)).find(Boolean);
        return { id: p.participant_id, label: p.label, status: p.status, stream: edge?.inbound || null, tags: p.summary?.tags || [] };
      });
    },
  },
  watch: {
    sessionUuid: { immediate: true, handler: 'load' },
  },
  methods: {
    async load(uuid) {
      this.loading = true;
      this.error = '';
      try {
        const data = await getCallQuality(uuid);
        if (uuid === this.sessionUuid) this.participants = data.participants;
      } catch {
        if (uuid === this.sessionUuid) this.error = 'Twilio call quality is unavailable right now.';
      } finally {
        if (uuid === this.sessionUuid) this.loading = false;
      }
    },
    tagStyle(tag) {
      const problem = PROBLEM_TAG.test(tag);
      return {
        display: 'inline-block', marginRight: '4px', padding: '0 6px', borderRadius: '9999px',
        background: problem ? '#FEF3C7' : '#F0F0F1', color: problem ? '#92400E' : '#6B7280',
      };
    },
    cell(colour = '#353537') {
      return { padding: '4px 8px', color: colour };
    },
    // Thresholds follow Twilio's guidance: MOS above 4 is good; 1% loss and 30 ms jitter are where audio degrades.
    mosColour(v) {
      if (v === null) return undefined;
      if (v < 3.5) return RED;
      return v < 4 ? AMBER : undefined;
    },
    lossColour(v) {
      if (v === null) return undefined;
      if (v >= 3) return RED;
      return v >= 1 ? AMBER : undefined;
    },
    jitterColour(v) {
      return v !== null && v >= 30 ? AMBER : undefined;
    },
    rttColour(v) {
      return v !== null && v !== undefined && v >= 300 ? AMBER : undefined;
    },
    fmt(v, digits) {
      return v === null || v === undefined ? '—' : v.toFixed(digits);
    },
    pct(v) {
      return v === null ? '—' : `${v.toFixed(1)}%`;
    },
    ms(v) {
      return v === null || v === undefined ? '—' : `${Math.round(v)} ms`;
    },
  },
};
</script>
